/**
 * The navigation of the application. **One definition, three renderers.**
 *
 * A desktop row of areas, a tablet row, and a phone's bottom bar are three
 * ways of drawing the same thing. They are drawn from this file alone: an
 * area, the sections inside it, the permission each one needs, the frameworks
 * that show it, and the icon that stands for it. Nothing renders a route that
 * is not here, and nothing repeats a permission rule of its own.
 *
 * A section with a permission appears only for a person the server granted
 * it. That is a courtesy, not a boundary: the server refuses the request as
 * well, and it refuses it whether or not the browser drew the link.
 *
 * `workspaces` names the frameworks that show a section. `null` means every
 * workspace, because the organization holds one of that record rather than
 * one for each framework.
 *
 * `group` is what a phone can show at once. Four groups fit across the bottom
 * of a phone; eight areas do not, so the areas a person opens less often are
 * gathered behind More. The grouping changes nothing about an address, a
 * permission, or which area a page belongs to on a desktop.
 */

import type { IconName } from "../components/icon-names.ts";

export interface Section {
  readonly to: string;
  /** The mark beside the label in every navigation that lists it. */
  readonly icon: IconName;
  readonly label: string;
  /**
   * The permission the page's own read needs on the server. A list means
   * any one of them. No permission means every member reads it.
   */
  readonly permission?: string | readonly string[];
  readonly workspaces: readonly string[] | null;
}

/** The four groups a phone shows: three tabs and the menu behind the fourth. */
export type NavigationGroup = "home" | "assess" | "records" | "more";

export interface Area {
  readonly key: string;
  readonly label: string;
  readonly permission?: string;
  readonly icon: IconName;
  readonly group: NavigationGroup;
  readonly sections: readonly Section[];
}

/**
 * The areas of the service, and the sections inside each one.
 *
 * Eight areas replace sixteen peers. Each area groups related work; it does
 * not merge pages, because a page that grows longer is not simpler. Every
 * address stays the same, so a link and a test written before this grouping
 * still work.
 *
 * Home holds what one person must act on: their own work and the approvals
 * that wait for them. Operations holds the work a person performs again and
 * again: a schedule comes due, a person acts, and the result is recorded,
 * and the plan of action, whose items come from a finding of NOT MET or from
 * vulnerability work. Assess holds the judgment of a requirement. Records
 * holds the registers that the organization keeps current, the exceptions
 * list among them. Platform administration is the operator's own
 * area, and a customer never reaches it.
 */
export const AREAS: readonly Area[] = [
  {
    key: "home",
    label: "Home",
    icon: "house",
    group: "home",
    sections: [
      { to: "/", icon: "my-work", label: "My work", workspaces: null },
      { to: "/approvals", icon: "approvals", label: "Approvals", permission: "approval.decide", workspaces: null },
      { to: "/requests", icon: "asks", label: "Asks", workspaces: null },
      { to: "/summary", icon: "summary", label: "Program summary", permission: "summary.read", workspaces: null },
      { to: "/dashboard", icon: "dashboard", label: "Executive dashboard", permission: "dashboard.read", workspaces: null },
    ],
  },
  {
    key: "assess",
    label: "Assess",
    icon: "clipboard",
    group: "assess",
    sections: [
      { to: "/statements", icon: "statements", label: "Implementation statements", permission: "assessment.read", workspaces: null },
      { to: "/assessments", icon: "assessments", label: "Assessments", permission: "assessment.read", workspaces: null },
      { to: "/evidence", icon: "evidence", label: "Evidence", permission: ["evidence.read", "evidence.submit"], workspaces: null },
      { to: "/audit-prep", icon: "third-party", label: "Third-party assessment", permission: "engagement.read", workspaces: ["cmmc-l2"] },
      { to: "/reports", icon: "reports", label: "Reports", permission: ["assessment.read", "report.read_released"], workspaces: null },
      { to: "/hipaa", icon: "hipaa", label: "HIPAA", permission: "assessment.read", workspaces: ["hipaa"] },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    icon: "gauge",
    // The work that comes round again — a monitoring result, a change, a
    // vulnerability — is a register the organization keeps, so a phone finds
    // it with the other registers rather than behind More.
    group: "records",
    sections: [
      { to: "/vulnerabilities", icon: "vulnerabilities", label: "Vulnerabilities", permission: "vulnerability.read", workspaces: null },
      // Moved from Assess on 2026-10-28 (owner): the plan is worked on with the vulnerabilities.
      { to: "/plan-of-action", icon: "plan", label: "Plan of action", permission: "risk.read", workspaces: null },
      { to: "/monitoring", icon: "monitoring", label: "Monitoring", permission: "monitoring.read", workspaces: null },
      // The operator's own organization holds the intake, read with operations.read.
      { to: "/backups", icon: "backups", label: "Backups", permission: ["monitoring.read", "operations.read"], workspaces: null },
      { to: "/audit-procedure", icon: "magnifier", label: "Audit", permission: "audit_procedure.read", workspaces: null },
      { to: "/tabletop", icon: "tabletop", label: "Tabletop exercises", permission: "monitoring.read", workspaces: null },
      { to: "/changes", icon: "changes", label: "Changes", permission: "change.read", workspaces: null },
    ],
  },
  {
    key: "records",
    label: "Records",
    icon: "folder",
    group: "records",
    sections: [
      { to: "/documents", icon: "documents", label: "Documents", permission: ["document.read", "document.read_released"], workspaces: null },
      { to: "/inventories", icon: "inventories", label: "Inventories", permission: "inventory.read", workspaces: null },
      { to: "/risks", icon: "risks", label: "Risks", permission: "risk.read", workspaces: null },
      { to: "/exceptions", icon: "flag", label: "Exceptions", permission: "risk.read", workspaces: null },
      { to: "/program-imports", icon: "import", label: "Import an existing program", permission: "program_import.read", workspaces: null },
      { to: "/trash", icon: "trash", label: "Trash", permission: "record.trash", workspaces: null },
    ],
  },
  {
    key: "engagements",
    label: "Engagements",
    icon: "handshake",
    group: "more",
    sections: [
      { to: "/engagements", icon: "handshake", label: "Engagements", permission: "engagement.read", workspaces: null },
      { to: "/public-page", icon: "globe", label: "Public page", permission: "trust_page.publish", workspaces: null },
    ],
  },
  {
    key: "organization",
    label: "Organization",
    icon: "building",
    group: "more",
    sections: [
      { to: "/people", icon: "people", label: "People", permission: "membership.read", workspaces: null },
      { to: "/providers", icon: "providers", label: "Providers and assessors", permission: "grant.manage", workspaces: null },
      { to: "/roles", icon: "roles", label: "Roles", permission: "membership.read", workspaces: null },
      { to: "/provider", icon: "key", label: "Customer access", permission: "provider.delegate", workspaces: null },
      { to: "/employees", icon: "employees", label: "Employees", permission: "employee.read", workspaces: null },
      { to: "/scope", icon: "scope", label: "Scope", permission: "system.read", workspaces: null },
      { to: "/external-connections", icon: "connections", label: "External connections", permission: "system.read", workspaces: null },
      /*
        The CUI flow builder is hidden, not removed. A data flow diagram is
        now an image a person draws where they like and uploads as a
        document — see the flow diagram in `routes/documents.tsx` — and the
        builder's own pages carry the diagrams already drawn in it. The
        addresses still work, so every existing diagram document opens.
      */
      { to: "/settings", icon: "settings", label: "Settings", permission: "organization.manage", workspaces: null },
    ],
  },
  {
    key: "security",
    label: "Security",
    icon: "shield",
    group: "more",
    sections: [
      { to: "/security", icon: "shield", label: "Security", permission: "security.org.read", workspaces: null },
      { to: "/security/helpdesk", icon: "helpdesk", label: "Helpdesk", permission: "security.org.support.read", workspaces: null },
      { to: "/security/platform", icon: "server", label: "Platform security", permission: "security.platform.read", workspaces: null },
    ],
  },
  {
    key: "platform-administration",
    label: "Platform administration",
    permission: "operations.read",
    icon: "tools",
    group: "more",
    sections: [
      { to: "/operations", icon: "tools", label: "Platform administration", permission: "operations.read", workspaces: null },
    ],
  },
];

/** The area that holds the current address. */
export function areaOf(pathname: string): Area {
  // The engagement workspace lives under /audit/<id>. It is part of the
  // third-party assessment, which is in Assess, and adds no navigation link
  // of its own. The assessor's own portal and the invitation page are not the
  // customer's area at all, so they stay with engagements.
  if (pathname.startsWith("/audit/portal") || pathname.startsWith("/audit/accept")) {
    return AREAS.find((area) => area.key === "engagements") ?? AREAS[0]!;
  }
  if (pathname.startsWith("/audit/")) {
    return AREAS.find((area) => area.key === "assess") ?? AREAS[0]!;
  }
  const found = AREAS.find((area) =>
    area.sections.some((section) =>
      section.to === "/" ? pathname === "/" : pathname.startsWith(section.to),
    ),
  );
  return found ?? AREAS[0]!;
}

/** The group a phone highlights for the current address. */
export function groupOf(pathname: string): NavigationGroup {
  return areaOf(pathname).group;
}

export interface Visibility {
  readonly permissions: readonly string[];
  /** The frameworks the organization has adopted and is using. */
  readonly activeFrameworks: readonly string[];
  /** The one framework in view, or null for every adopted framework. */
  readonly workspaceKey: string | null;
}

/** Whether the permissions include the one named, or any one of a list. */
export function holdsAny(needed: string | readonly string[] | undefined, permissions: readonly string[]): boolean {
  if (needed === undefined) return true;
  const list: readonly string[] = typeof needed === "string" ? [needed] : needed;
  return list.some((permission) => permissions.includes(permission));
}

/** Whether a person in this organization sees a section at all. */
export function sectionVisible(section: Section, where: Visibility): boolean {
  if (!holdsAny(section.permission, where.permissions)) return false;
  if (section.workspaces === null) return true;
  // A section of one framework shows in that workspace. With no workspace
  // selected, every adopted framework's section shows.
  return where.workspaceKey === null
    ? section.workspaces.some((key) => where.activeFrameworks.includes(key))
    : section.workspaces.includes(where.workspaceKey);
}

/** The sections of one area that this person sees. */
export function visibleSections(area: Area, where: Visibility): readonly Section[] {
  return area.sections.filter((section) => sectionVisible(section, where));
}

/** The areas this person sees, in order. */
export function visibleAreas(where: Visibility): readonly Area[] {
  return AREAS.filter(
    (area) =>
      holdsAny(area.permission, where.permissions) &&
      visibleSections(area, where).length > 0,
  );
}

/** The areas of one phone group that this person sees. */
export function areasInGroup(group: NavigationGroup, where: Visibility): readonly Area[] {
  return visibleAreas(where).filter((area) => area.group === group);
}

/**
 * The first address a phone group opens, or null when the person sees none.
 *
 * A group can hold more than one area — Records holds the registers and the
 * work that comes round again — and the tab should open the page the tab is
 * named after. So the area whose key matches the group goes first, and the
 * others follow in their own order.
 */
export function firstRouteOf(group: NavigationGroup, where: Visibility): string | null {
  const areas = [...areasInGroup(group, where)].sort((a, b) =>
    a.key === group ? -1 : b.key === group ? 1 : 0,
  );
  for (const area of areas) {
    const section = visibleSections(area, where)[0];
    if (section !== undefined) return section.to;
  }
  return null;
}

/** The three groups a phone shows as tabs, with the label and icon of each. */
export const PHONE_TABS: readonly {
  readonly group: NavigationGroup;
  readonly label: string;
  readonly icon: IconName;
}[] = [
  { group: "home", label: "Home", icon: "house" },
  { group: "assess", label: "Assess", icon: "clipboard" },
  { group: "records", label: "Records", icon: "folder" },
];
